import React, { useState, useEffect, useCallback, useReducer } from 'react'
import { Flex, Box } from 'reflexbox/styled-components'
import { toast } from 'react-toastify'
import { Link } from 'react-router-dom'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'

import DataListingTable from 'components/DataListingTable'
import Loader from 'components/Loader'
import { SearchInput, NumberField } from 'components/Form'
import Button from 'components/Button'
import { DefaultModal } from 'components/Modal'
import Card from 'components/Card'
import { Close as CloseIcon } from 'components/Icons'
import { SemiTitle } from 'components/Typography'

import { useDebounce } from 'hooks/debounce'
import { reducer as loadingReducer } from 'helpers/loadingReducer'
import { getPartners } from 'services/partners'
import * as settingsService from 'services/settings'

import { colors } from 'styles/theme'
import { CloseButton } from './styles'

const tableColumns = [
  {
    Header: 'Razão Social',
    accessor: 'company_name',
  },
  {
    Header: 'CNPJ',
    accessor: 'document',
  },
  {
    Header: 'Responsável',
    accessor: 'responsible_name',
  },
  {
    Header: 'E-mail',
    accessor: 'email',
  },
  {
    Header: 'Telefone',
    accessor: 'phone',
  },
]

const partnerGlobalDiscountFormSchema = Yup.object().shape({
  discount_value: Yup.number()
    .max(100, 'Desconto máximo é de 100%')
    .required(),
})

const PartnersList = () => {
  const [partners, setPartners] = useState([])
  const [search, setSearch] = useState(null)
  const [requestStatus, dispatch] = useReducer(loadingReducer, { isLoading: true, error: false })
  const [isLoading, setLoading] = useState(false)
  const [isPartnerGlobalDiscountDefinitionModalOpen, setPartnerGlobalDiscountDefinitionModalOpen] = useState(false)
  const [currentPartnerGlobalDiscount, setCurrentPartnerGlobalDiscount] = useState(0)
  const [sortBy, setSortBy] = useState(null)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalRecords: 0,
  })

  const handleTableDataFetch = ({ sortBy, currentPage, pageSize }) => {
    setSortBy(sortBy)
    setPagination({ ...pagination, currentPage, pageSize })
  }

  const debouncedSearchValue = useDebounce(search, 500)

  const fetchPartners = useCallback(async () => {
    try {
      if (debouncedSearchValue !== null) {
        setLoading(true)
      }
      dispatch({ type: 'loading' })
      const res = await getPartners({ search: debouncedSearchValue, sortBy, pagination })

      const formattedData = res.partners.map(
        ({ id, company_name, responsible_name, document, user: { email }, phones: [phone] }) => ({
          id,
          company_name: <Link to={`/staff/partners/list/${id}`}>{company_name}</Link>,
          responsible_name,
          document: document.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/g, '$1.$2.$3/$4-$5'),
          email,
          phone: phone.number,
        }),
      )
      setPartners(formattedData)
      setPagination(res.pagination)

      dispatch({ type: 'success' })
      setLoading(false)
    } catch (err) {
      console.log(err)
      toast.error('Não foi possível carregar os parceiros no momento')
      dispatch({ type: 'error' })
      setLoading(false)
    }
  }, [pagination.currentPage, pagination.pageSize, debouncedSearchValue])

  const handleSubmitPartnerGlobalDiscount = async ({ discount_value }) => {
    if (discount_value !== currentPartnerGlobalDiscount) {
      try {
        await settingsService.setPartnerGlobalDiscount(discount_value)

        toast.success('Desconto global de parceiros atualizado com sucesso!')
        setPartnerGlobalDiscountDefinitionModalOpen(false)
        setCurrentPartnerGlobalDiscount(discount_value)
      } catch (e) {
        console.log(e)
        toast.error('Não foi possível atualizar o desconto global de parceiros.')
      }
    } else {
      setPartnerGlobalDiscountDefinitionModalOpen(false)
    }
  }

  useEffect(() => {
    const fetchCurrentPartnerGlobalDiscount = async () => {
      try {
        const discount = await settingsService.getParterGlobalDiscount()
        setCurrentPartnerGlobalDiscount(discount)
      } catch (e) {
        console.log(e)
        toast.error('Não foi possível carregar o desconto global de parceiros.')
      }
    }

    fetchCurrentPartnerGlobalDiscount()
  }, [])

  useEffect(() => {
    if (sortBy) {
      fetchPartners()
    }
  }, [sortBy, pagination.currentPage, pagination.pageSize])

  useEffect(() => {
    if (pagination.currentPage === 1 && debouncedSearchValue) {
      fetchPartners()
    }
    setPagination({ ...pagination, currentPage: 1 })
  }, [debouncedSearchValue])

  return (
    <>
      <Flex width={1} flexDirection='column'>
        <Flex width={1} justifyContent='space-between' alignItems='center'>
          <h1>Parceiros Pré Pago</h1>
        </Flex>

        <Flex alignItems='center' justifyContent='space-between' mb={24}>
          <Flex>
            <SearchInput
              name='search'
              label='Buscar'
              placeholder='Buscar'
              onChange={e => setSearch(e.target.value)}
              mr={24}
            />
            {isLoading && <Loader />}
          </Flex>

          <Flex>
            <Button
              style={{ width: 'auto' }}
              mr='24px'
              onClick={() => setPartnerGlobalDiscountDefinitionModalOpen(true)}
            >
              Desconto Padrão
            </Button>

            <Button as={Link} to='/staff/partners/list/orders/new' style={{ width: 'auto' }}>
              Dar crédito de pacotes
            </Button>
          </Flex>
        </Flex>

        <DataListingTable
          columns={tableColumns}
          data={partners}
          pagination={pagination}
          loading={requestStatus.isLoading}
          fetchData={handleTableDataFetch}
        />
      </Flex>
      <DefaultModal
        isOpened={isPartnerGlobalDiscountDefinitionModalOpen}
        setIsOpened={setPartnerGlobalDiscountDefinitionModalOpen}
      >
        <Card backgroundColor={colors.white} width={480}>
          <Flex flexDirection='column'>
            <Flex alignItems='center' justifyContent='space-between' mb={20}>
              <SemiTitle>Definir desconto padrão de parceiros</SemiTitle>
              <CloseButton onClick={() => setPartnerGlobalDiscountDefinitionModalOpen(false)}>
                <CloseIcon />
              </CloseButton>
            </Flex>
            <Formik
              initialValues={{ discount_value: currentPartnerGlobalDiscount }}
              onSubmit={handleSubmitPartnerGlobalDiscount}
              validationSchema={partnerGlobalDiscountFormSchema}
            >
              {({ isSubmitting }) => (
                <Form>
                  <Box mb={30}>
                    <NumberField
                      label='Desconto Padrão'
                      name='discount_value'
                      placeholder='10,00 %'
                      suffix=' %'
                      decimalScale='2'
                    />
                  </Box>
                  <Flex alignItems='center' justifyContent='flex-end'>
                    <Button outlined onClick={() => setPartnerGlobalDiscountDefinitionModalOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type='submit' marginLeft={12} disabled={isSubmitting}>
                      {isSubmitting ? <Loader /> : 'Salvar'}
                    </Button>
                  </Flex>
                </Form>
              )}
            </Formik>
          </Flex>
        </Card>
      </DefaultModal>
    </>
  )
}

export default PartnersList
